import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Plus, Trophy } from 'lucide-react';
import { hasPermission } from '../../utils/permissions';

export default function AdminScoringRules() {
  const { user, currentSeason, API, tenantContext } = useContext(AppContext);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({ event_name: '', points: '', notes: '' });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showDefaultRulesDialog, setShowDefaultRulesDialog] = useState(false);
  const [loadingDefaultRules, setLoadingDefaultRules] = useState(false);
  const [defaultRulesPreview, setDefaultRulesPreview] = useState([]);

  useEffect(() => {
    if (currentSeason) {
      loadRules();
    }
  }, [currentSeason]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(`${API}/seasons/${currentSeason.id}/scoring-rules`);
      setRules(response.data);
    } catch (error) {
      console.error('Failed to load rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultRulesPreview = async () => {
    try {
      const response = await authAxios.get(`${API}/platform/default-scoring-rules`);
      setDefaultRulesPreview(response.data);
    } catch (error) {
      console.error('Failed to load default rules preview:', error);
    }
  };

  const addRule = async () => {
    if (!newRule.event_name || !newRule.points) return;
    
    try {
      await authAxios.post(
        `${API}/scoring-rules`,
        {
          season_id: currentSeason.id,
          event_name: newRule.event_name,
          points: parseInt(newRule.points),
          notes: newRule.notes || null
        },
        { withCredentials: true }
      );
      toast.success('Scoring rule added!');
      setShowAddRule(false);
      setNewRule({ event_name: '', points: '', notes: '' });
      loadRules();
    } catch (error) {
      console.error('Failed to add rule:', error);
      toast.error('Failed to add rule');
    }
  };

  const updateRule = async (ruleId, field, value) => {
    try {
      await authAxios.patch(
        `${API}/scoring-rules/${ruleId}`,
        { [field]: field === 'points' ? parseInt(value) : value },
        { withCredentials: true }
      );
      
      toast.success('Rule updated! (Past event logs unchanged)');
      loadRules();
    } catch (error) {
      console.error('Failed to update rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to update rule');
    }
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;
    
    try {
      await authAxios.delete(`${API}/scoring-rules/${ruleToDelete.id}`);
      toast.success('Rule deleted');
      setShowDeleteDialog(false);
      setRuleToDelete(null);
      loadRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
      setShowDeleteDialog(false);
    }
  };

  const addDefaultRules = async () => {
    try {
      setLoadingDefaultRules(true);
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/apply-default-rules`,
        {},
        { withCredentials: true }
      );
      toast.success(`Added ${response.data.added_count} default scoring rules!`);
      if (response.data.skipped_count > 0) {
        toast.info(`${response.data.skipped_count} rules skipped (already exist)`);
      }
      setShowDefaultRulesDialog(false);
      loadRules();
    } catch (error) {
      console.error('Failed to add default rules:', error);
      toast.error(error.response?.data?.detail || 'Failed to add default rules');
    } finally {
      setLoadingDefaultRules(false);
    }
  };

  // Permission check - Only League Admins can manage scoring rules
  const canManageScoringRules = hasPermission('MANAGE_SCORING_RULES', tenantContext, user);

  if (!canManageScoringRules) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">Only League Admins can manage scoring rules.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto container-mobile">
        <div className="spacing-section flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Scoring Rules
            </h1>
            <p className="text-responsive text-slate-600">Season {currentSeason?.number} Point System</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <Dialog open={showDefaultRulesDialog} onOpenChange={(open) => {
              setShowDefaultRulesDialog(open);
              if (open) loadDefaultRulesPreview();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Trophy className="w-4 h-4 mr-2" />
                  Add Default Rules
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Platform Default Scoring Rules</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-600">
                    This will add the platform default scoring rules to Season {currentSeason?.number}.
                  </p>
                  
                  {/* Preview of default rules */}
                  {defaultRulesPreview.length > 0 && (
                    <div className="bg-slate-50 border rounded-lg p-3 max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-slate-700 mb-2">Rules to be added:</p>
                      <div className="space-y-1">
                        {defaultRulesPreview.map((rule, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-600">{rule.event_name}</span>
                            <span className={`font-medium ${rule.points >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {rule.points >= 0 ? '+' : ''}{rule.points} pts
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-slate-500">
                    <strong>Note:</strong> Rules that already exist will be skipped (no duplicates).
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      ⚠️ <strong>Warning:</strong> If you already have custom rules, the default rules will be added alongside them.
                    </p>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setShowDefaultRulesDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={addDefaultRules} disabled={loadingDefaultRules}>
                      {loadingDefaultRules ? 'Adding...' : `Add ${defaultRulesPreview.length} Rules`}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Rule
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Scoring Rule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Event Name</Label>
                    <Input
                      value={newRule.event_name}
                      onChange={(e) => setNewRule({...newRule, event_name: e.target.value})}
                      placeholder="e.g., Individual Reward/Immunity Challenge Win"
                    />
                  </div>
                  <div>
                    <Label>Points</Label>
                    <Input
                      type="number"
                      value={newRule.points}
                      onChange={(e) => setNewRule({...newRule, points: e.target.value})}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={newRule.notes}
                      onChange={(e) => setNewRule({...newRule, notes: e.target.value})}
                      placeholder="Additional details..."
                    />
                  </div>
                  <Button className="w-full" onClick={addRule}>Add Rule</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              </div>
            ) : (
              <div>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Note:</strong> Updating rules only affects future events. Past event log entries keep their original values.
                  </p>
                </div>
                <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th className="text-center">Points</th>
                      <th>Notes</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-slate-600">
                          No scoring rules yet. Add some to get started!
                        </td>
                      </tr>
                    ) : (
                      [...rules].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(rule => (
                        <tr key={rule.id}>
                          <td className="font-medium">
                            {editingRule === rule.id ? (
                              <Input
                                defaultValue={rule.event_name}
                                onBlur={(e) => {
                                  if (e.target.value !== rule.event_name) {
                                    updateRule(rule.id, 'event_name', e.target.value);
                                  }
                                  setEditingRule(null);
                                }}
                                autoFocus
                              />
                            ) : (
                              <span onClick={() => setEditingRule(rule.id)} className="cursor-pointer hover:text-amber-600">
                                {rule.event_name}
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <Input
                              type="number"
                              defaultValue={rule.points}
                              onBlur={(e) => {
                                if (parseInt(e.target.value) !== rule.points) {
                                  updateRule(rule.id, 'points', e.target.value);
                                }
                              }}
                              className={`w-20 text-center font-bold ${
                                rule.points > 0 ? 'text-emerald-600' :
                                rule.points < 0 ? 'text-red-600' :
                                'text-slate-600'
                              }`}
                            />
                          </td>
                          <td>
                            <Input
                              defaultValue={rule.notes || ''}
                              onBlur={(e) => {
                                if (e.target.value !== rule.notes) {
                                  updateRule(rule.id, 'notes', e.target.value);
                                }
                              }}
                              placeholder="Add notes..."
                              className="text-sm"
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClick(rule)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Scoring Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Are you sure you want to delete the rule <strong>&quot;{ruleToDelete?.event_name}&quot;</strong>?
            </p>
            <p className="text-sm text-slate-600">
              This action cannot be undone. If this rule is used in any event log entries, the deletion will be blocked.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
